'use client'

import { useEffect, useState } from 'react'
import { MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WhatsAppWidgetProps {
    settings: {
        enabled?: boolean
        phoneNumber?: string
        welcomeMessage?: string
        pulseEffect?: boolean
    }
}

export function WhatsAppWidget({ settings }: WhatsAppWidgetProps) {
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        if (settings?.enabled && settings?.phoneNumber) {
            // Small delay for entrance animation
            const timer = setTimeout(() => setIsVisible(true), 1000)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [settings])

    if (!settings?.enabled || !settings?.phoneNumber) return null

    // Format phone number: remove non-digits
    const cleanPhone = settings.phoneNumber.replace(/\D/g, '')
    const message = encodeURIComponent(settings.welcomeMessage || '')
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`

    return (
        <div className={cn(
            "fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4 transition-all duration-500 transform",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        )}>
            {/* Direct Button Approach (Simple & Effective) */}
            <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex items-center justify-center w-14 h-14 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110"
                aria-label="Chat on WhatsApp"
            >
                <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
                <MessageCircle className="w-8 h-8" />

                {/* Ping Animation - Conditional */}
                {settings.pulseEffect && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75 animate-ping -z-10"></span>
                )}
            </a>
        </div>
    )
}
